import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  TextInput,
  Skeleton,
  Card,
  SimpleGrid,
  Button,
} from '@mantine/core';
import {
  IconSearch,
  IconFileDescription,
  IconEye,
  IconCalendar,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { useBidResponses as useBidResponsesApi } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

interface BidResponse {
  id: number;
  bid_number: string;
  valid_from: string;
  valid_until: string;
  status: string;
  total_estimated_value: string;
}

type SortField = 'bid_number' | 'valid_from' | 'valid_until' | 'status' | 'total_estimated_value';

export default function BidResponses() {
  const [search, setSearch] = useState('');
  const [sortField] = useState<SortField>('valid_from');
  const [sortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading } = useBidResponsesApi();
  const items: BidResponse[] = (data?.items ?? []) as BidResponse[];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.bid_number.toLowerCase().includes(s) ||
          r.status.toLowerCase().includes(s),
      );
    }
    result.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField];
      const bv = (b as unknown as Record<string, unknown>)[sortField];
      const cmp = (av as string) < (bv as string) ? -1 : (av as string) > (bv as string) ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const getValidityBadge = (validUntil: string) => {
    const daysRemaining = dayjs(validUntil).diff(dayjs(), 'day');
    if (daysRemaining < 0) {
      return (
        <Badge size="xs" variant="light" color="red">
          expired
        </Badge>
      );
    }
    if (daysRemaining <= 7) {
      return (
        <Badge size="xs" variant="light" color="orange">
          {daysRemaining}d left
        </Badge>
      );
    }
    return (
      <Badge size="xs" variant="light" color="green">
        {daysRemaining}d left
      </Badge>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Bid Responses"
        description="View your bid and quote responses"
      />

      <TextInput
        placeholder="Search by bid number or status..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height={200} radius="md" />
          ))}
        </SimpleGrid>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconFileDescription size={30} />}
          title="No bid responses"
          description={
            search
              ? 'No bids match your search criteria'
              : 'You have no bid or quote responses yet'
          }
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {filtered.map((bid) => (
            <Card key={bid.id} shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Text size="lg" fw={600}>
                      {bid.bid_number}
                    </Text>
                  </div>
                  <StatusBadge status={bid.status} />
                </Group>

                <Paper p="sm" bg="gray.0" radius="sm">
                  <Text size="xl" fw={700} ta="center">
                    ${parseFloat(bid.total_estimated_value).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Estimated Value
                  </Text>
                </Paper>

                <Stack gap={4}>
                  <Group gap="xs">
                    <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      Valid from:{' '}
                      <Text span fw={500} c="dark">
                        {dayjs(bid.valid_from).format('MMM D, YYYY')}
                      </Text>
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconCalendar size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      Valid until:{' '}
                      <Text span fw={500} c="dark">
                        {dayjs(bid.valid_until).format('MMM D, YYYY')}
                      </Text>
                    </Text>
                    {getValidityBadge(bid.valid_until)}
                  </Group>
                </Stack>

                <Button
                  variant="light"
                  fullWidth
                  leftSection={<IconEye size={16} />}
                  mt="xs"
                >
                  View Details
                </Button>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
